import { useState, useCallback, useMemo } from 'react';
import { arrayMove } from '@dnd-kit/sortable';
import type {
  ScriptStep,
  ScriptStepWithId,
  ItemScriptData,
  MasterScriptData,
  TestPointRef,
  StepType,
  ItemRef
} from '../types/steps';
import { hydrateMasterScriptData } from '../types/steps';
import { getStepDefaults } from '../utils/stepDefaults';
import {
  nextStepId,
  toStepsWithId,
  reindex,
  stripForPersistence
} from './useStepArray';

type StepSection =
  | 'steps'
  | 'pickupSteps'
  | 'returnSteps'
  | 'beforeSteps'
  | 'calibrationSteps'
  | 'afterSteps';

type UseScriptBuilderReturn = {
  steps: ScriptStepWithId[];
  pickupSteps: ScriptStepWithId[];
  returnSteps: ScriptStepWithId[];
  beforeSteps: ScriptStepWithId[];
  calibrationSteps: ScriptStepWithId[];
  afterSteps: ScriptStepWithId[];
  items: ItemRef[];
  testPointRef: TestPointRef | null;
  isItemScript: boolean;
  isMasterScript: boolean;
  isDirty: boolean;
  parseError: string | null;
  addStep: (type: StepType, section?: StepSection) => void;
  removeStep: (id: string, section?: StepSection) => void;
  updateStep: (
    id: string,
    updates: Partial<ScriptStep>,
    section?: StepSection
  ) => void;
  reorderSteps: (
    activeId: string,
    overId: string,
    section?: StepSection
  ) => void;
  replaceAllSteps: (steps: ScriptStep[]) => void;
  setTestPointRef: (ref: TestPointRef) => void;
  setItems: (items: ItemRef[]) => void;
  addItems: (newItems: ItemRef[]) => void;
  removeItem: (scriptId: string) => void;
  reorderItems: (activeId: string, overId: string) => void;
  getScriptData: () => string;
  toJson: () => string;
  loadScriptData: (json: string) => void;
  markClean: () => void;
  reset: () => void;
};

export const useScriptBuilder = (
  initialJson?: string | null
): UseScriptBuilderReturn => {
  const initial = useMemo(() => {
    if (!initialJson)
      return {
        steps: [],
        pickupSteps: [],
        returnSteps: [],
        beforeSteps: [],
        calibrationSteps: [],
        afterSteps: [],
        items: [] as ItemRef[],
        testPointRef: null,
        isItemScript: false,
        isMasterScript: false,
        parseError: null
      };
    try {
      const parsed = JSON.parse(initialJson);

      if (parsed.testPointRef) {
        const itemData = parsed as ItemScriptData;
        return {
          steps: [],
          pickupSteps: [],
          returnSteps: [],
          beforeSteps: toStepsWithId(itemData.beforeSteps ?? []),
          calibrationSteps: toStepsWithId(itemData.calibrationSteps ?? []),
          afterSteps: toStepsWithId(itemData.afterSteps ?? []),
          items: [] as ItemRef[],
          testPointRef: itemData.testPointRef,
          isItemScript: true,
          isMasterScript: false,
          parseError: null
        };
      }

      const isMaster =
        'pickupSteps' in parsed ||
        'returnSteps' in parsed ||
        'items' in parsed ||
        'setupSteps' in parsed ||
        'teardownSteps' in parsed ||
        'cells' in parsed;

      if (isMaster) {
        const masterData = hydrateMasterScriptData(
          parsed as Record<string, unknown>
        );
        return {
          steps: [],
          pickupSteps: toStepsWithId(masterData.pickupSteps),
          returnSteps: toStepsWithId(masterData.returnSteps),
          beforeSteps: [],
          calibrationSteps: [],
          afterSteps: [],
          items: masterData.items,
          testPointRef: null,
          isItemScript: false,
          isMasterScript: true,
          parseError: null
        };
      }

      return {
        steps: toStepsWithId((parsed.steps as ScriptStep[]) ?? []),
        pickupSteps: [],
        returnSteps: [],
        beforeSteps: [],
        calibrationSteps: [],
        afterSteps: [],
        items: [] as ItemRef[],
        testPointRef: null,
        isItemScript: false,
        isMasterScript: false,
        parseError: null
      };
    } catch (e) {
      return {
        steps: [],
        pickupSteps: [],
        returnSteps: [],
        beforeSteps: [],
        calibrationSteps: [],
        afterSteps: [],
        items: [] as ItemRef[],
        testPointRef: null,
        isItemScript: false,
        isMasterScript: false,
        parseError: e instanceof Error ? e.message : 'Invalid JSON'
      };
    }
  }, [initialJson]);

  const [steps, setSteps] = useState<ScriptStepWithId[]>(initial.steps);
  const [pickupSteps, setPickupSteps] = useState<ScriptStepWithId[]>(
    initial.pickupSteps
  );
  const [returnSteps, setReturnSteps] = useState<ScriptStepWithId[]>(
    initial.returnSteps
  );
  const [beforeSteps, setBeforeSteps] = useState<ScriptStepWithId[]>(
    initial.beforeSteps
  );
  const [calibrationSteps, setCalibrationSteps] = useState<ScriptStepWithId[]>(
    initial.calibrationSteps
  );
  const [afterSteps, setAfterSteps] = useState<ScriptStepWithId[]>(
    initial.afterSteps
  );
  const [itemsState, setItemsState] = useState<ItemRef[]>(initial.items);
  const [testPointRef, setTestPointRefState] = useState<TestPointRef | null>(
    initial.testPointRef
  );
  const [isItemScript] = useState(initial.isItemScript);
  const [isDirty, setIsDirty] = useState(false);
  const [parseError] = useState<string | null>(initial.parseError);

  const isMasterScript =
    initial.isMasterScript ||
    itemsState.length > 0 ||
    pickupSteps.length > 0 ||
    returnSteps.length > 0;

  const getSetterForSection = useCallback((section: StepSection) => {
    if (section === 'pickupSteps') return setPickupSteps;
    if (section === 'returnSteps') return setReturnSteps;
    if (section === 'beforeSteps') return setBeforeSteps;
    if (section === 'calibrationSteps') return setCalibrationSteps;
    if (section === 'afterSteps') return setAfterSteps;
    return setSteps;
  }, []);

  const addStep = useCallback(
    (type: StepType, section: StepSection = 'steps') => {
      const defaults = getStepDefaults(type);
      const setter = getSetterForSection(section);
      setter(prev =>
        reindex([
          ...prev,
          {
            ...defaults,
            id: nextStepId(),
            index: prev.length
          } as ScriptStepWithId
        ])
      );
      setIsDirty(true);
    },
    [getSetterForSection]
  );

  const removeStep = useCallback(
    (id: string, section: StepSection = 'steps') => {
      const setter = getSetterForSection(section);
      setter(prev => reindex(prev.filter(s => s.id !== id)));
      setIsDirty(true);
    },
    [getSetterForSection]
  );

  const updateStep = useCallback(
    (
      id: string,
      updates: Partial<ScriptStep>,
      section: StepSection = 'steps'
    ) => {
      const setter = getSetterForSection(section);
      setter(prev =>
        prev.map(s =>
          s.id === id ? ({ ...s, ...updates } as ScriptStepWithId) : s
        )
      );
      setIsDirty(true);
    },
    [getSetterForSection]
  );

  const reorderSteps = useCallback(
    (activeId: string, overId: string, section: StepSection = 'steps') => {
      const setter = getSetterForSection(section);
      setter(prev => {
        const oldIndex = prev.findIndex(s => s.id === activeId);
        const newIndex = prev.findIndex(s => s.id === overId);
        if (oldIndex === -1 || newIndex === -1) return prev;
        return reindex(arrayMove(prev, oldIndex, newIndex));
      });
      setIsDirty(true);
    },
    [getSetterForSection]
  );

  const replaceAllSteps = useCallback((newSteps: ScriptStep[]) => {
    setSteps(toStepsWithId(newSteps));
    setIsDirty(true);
  }, []);

  const setTestPointRef = useCallback((ref: TestPointRef) => {
    setTestPointRefState(ref);
    setIsDirty(true);
  }, []);

  const setItems = useCallback((newItems: ItemRef[]) => {
    setItemsState(newItems);
    setIsDirty(true);
  }, []);

  const addItems = useCallback((newItems: ItemRef[]) => {
    setItemsState(prev => [...prev, ...newItems]);
    setIsDirty(true);
  }, []);

  const removeItem = useCallback((scriptId: string) => {
    setItemsState(prev => prev.filter(i => i.scriptId !== scriptId));
    setIsDirty(true);
  }, []);

  const reorderItems = useCallback((activeId: string, overId: string) => {
    setItemsState(prev => {
      const oldIndex = prev.findIndex(i => i.scriptId === activeId);
      const newIndex = prev.findIndex(i => i.scriptId === overId);
      if (oldIndex === -1 || newIndex === -1) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
    setIsDirty(true);
  }, []);

  const getScriptData = useCallback((): string => {
    if (isItemScript && testPointRef) {
      const data: ItemScriptData = {
        testPointRef,
        beforeSteps: stripForPersistence(beforeSteps),
        calibrationSteps: stripForPersistence(calibrationSteps),
        afterSteps: stripForPersistence(afterSteps)
      };
      return JSON.stringify(data, null, 2);
    }

    if (isMasterScript) {
      const data = {
        pickupSteps: stripForPersistence(pickupSteps),
        items: itemsState,
        returnSteps: stripForPersistence(returnSteps)
      } satisfies MasterScriptData;
      return JSON.stringify(data, null, 2);
    }

    const data = {
      steps: stripForPersistence(steps)
    };
    return JSON.stringify(data, null, 2);
  }, [
    isItemScript,
    isMasterScript,
    testPointRef,
    beforeSteps,
    calibrationSteps,
    afterSteps,
    steps,
    pickupSteps,
    returnSteps,
    itemsState
  ]);

  const toJson = getScriptData;

  const loadScriptData = useCallback((json: string) => {
    try {
      const parsed = JSON.parse(json);

      if (parsed.testPointRef) {
        const itemData = parsed as ItemScriptData;
        setBeforeSteps(toStepsWithId(itemData.beforeSteps ?? []));
        setCalibrationSteps(toStepsWithId(itemData.calibrationSteps ?? []));
        setAfterSteps(toStepsWithId(itemData.afterSteps ?? []));
        setTestPointRefState(itemData.testPointRef);
      } else {
        const masterCheck =
          'pickupSteps' in parsed ||
          'returnSteps' in parsed ||
          'items' in parsed ||
          'setupSteps' in parsed ||
          'teardownSteps' in parsed ||
          'cells' in parsed;

        if (masterCheck) {
          const masterData = hydrateMasterScriptData(
            parsed as Record<string, unknown>
          );
          setPickupSteps(toStepsWithId(masterData.pickupSteps));
          setReturnSteps(toStepsWithId(masterData.returnSteps));
          setItemsState(masterData.items);
        } else {
          setSteps(
            toStepsWithId((parsed.steps as ScriptStep[] | undefined) ?? [])
          );
        }
      }
      setIsDirty(false);
    } catch {
      // invalid JSON — caller should handle
    }
  }, []);

  const markClean = useCallback(() => {
    setIsDirty(false);
  }, []);

  const reset = useCallback(() => {
    setSteps(initial.steps);
    setPickupSteps(initial.pickupSteps);
    setReturnSteps(initial.returnSteps);
    setBeforeSteps(initial.beforeSteps);
    setCalibrationSteps(initial.calibrationSteps);
    setAfterSteps(initial.afterSteps);
    setItemsState(initial.items);
    setTestPointRefState(initial.testPointRef);
    setIsDirty(false);
  }, [initial]);

  return {
    steps,
    pickupSteps,
    returnSteps,
    beforeSteps,
    calibrationSteps,
    afterSteps,
    items: itemsState,
    testPointRef,
    isItemScript,
    isMasterScript,
    isDirty,
    parseError,
    addStep,
    removeStep,
    updateStep,
    reorderSteps,
    replaceAllSteps,
    setTestPointRef,
    setItems,
    addItems,
    removeItem,
    reorderItems,
    getScriptData,
    toJson,
    loadScriptData,
    markClean,
    reset
  };
};
